<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h3 class="text-h5">Gestión de Transferencias</h3>
          <v-btn color="primary" @click="showCreateDialog = true" prepend-icon="mdi-plus">
            Nueva Transferencia
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row>
      <v-col cols="12" md="4">
        <v-select
          v-model="filtroEstado"
          :items="estadosTransferencia"
          label="Filtrar por Estado"
          clearable
          @update:model-value="fetchTransferencias"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="busqueda"
          label="Buscar"
          prepend-inner-icon="mdi-magnify"
          clearable
          @update:model-value="fetchTransferencias"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-btn block color="secondary" @click="showScanDialog = true" prepend-icon="mdi-qrcode-scan">
          Escanear QR para Recepción
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla de Transferencias -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="transferencias"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.fecha_transferencia="{ item }">
            {{ formatDate(item.fecha_transferencia) }}
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip :color="getEstadoColor(item.estado)" size="small">
              {{ getEstadoLabel(item.estado) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="item.estado === 'Pendiente'"
              icon="mdi-qrcode"
              size="small"
              color="primary"
              @click="showQR(item)"
              title="Ver Código QR"
            ></v-btn>
            <v-btn
              v-if="item.estado === 'Pendiente'"
              icon="mdi-check"
              size="small"
              color="success"
              @click="recibirTransferencia(item)"
              title="Recibir Transferencia"
            ></v-btn>
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="verDetalle(item)"
              title="Ver Detalle"
            ></v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Diálogo: Nueva Transferencia -->
    <v-dialog v-model="showCreateDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Nueva Transferencia de DM</v-card-title>
        <v-card-text>
          <v-form ref="createForm">
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="nuevaTransferencia.inventory_item_id"
                  :items="dispositivosDisponibles"
                  item-title="descripcion"
                  item-value="id"
                  label="Dispositivo Médico *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-subtitle>
                        Lote: {{ item.raw.lote }} | S/N: {{ item.raw.serial_number || 'N/A' }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="nuevaTransferencia.servicio_clinico_destino_id"
                  :items="serviciosClinicos"
                  item-title="nombre"
                  item-value="id"
                  label="Servicio Clínico Destino *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                >
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-plus"
                      size="small"
                      @click="showAddServicioDialog = true"
                      title="Agregar Servicio"
                    ></v-btn>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevaTransferencia.usuario_receptor"
                  label="Usuario Receptor *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevaTransferencia.fecha_estimada_recepcion"
                  label="Fecha Estimada de Recepción"
                  type="date"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="nuevaTransferencia.observaciones"
                  label="Observaciones"
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelCreate">Cancelar</v-btn>
          <v-btn color="primary" @click="crearTransferencia" :loading="submitting">
            Crear y Generar QR
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Mostrar QR -->
    <v-dialog v-model="showQRDialog" max-width="500">
      <v-card>
        <v-card-title>Código QR de Transferencia</v-card-title>
        <v-card-text class="text-center">
          <div v-if="qrCode" class="my-4">
            <img :src="qrCode" alt="QR Code" style="max-width: 100%;" />
          </div>
          <p class="text-caption">
            ID Transferencia: {{ selectedTransferencia?.id }}
          </p>
          <p class="text-body-2">
            Presente este código al receptor para confirmar la recepción
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="downloadQR" color="secondary" prepend-icon="mdi-download">
            Descargar
          </v-btn>
          <v-btn @click="showQRDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Escanear QR -->
    <v-dialog v-model="showScanDialog" max-width="600">
      <v-card>
        <v-card-title>Escanear QR de Transferencia</v-card-title>
        <v-card-text>
          <div class="text-center my-4">
            <v-icon size="100" color="primary">mdi-qrcode-scan</v-icon>
            <p class="mt-4">Escanee el código QR de la transferencia</p>
            <v-text-field
              v-model="qrCodeScanned"
              label="O ingrese el código manualmente"
              prepend-inner-icon="mdi-barcode"
              @keyup.enter="procesarQRRecepcion"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showScanDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="procesarQRRecepcion" :loading="submitting">
            Confirmar Recepción
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Agregar Servicio Clínico -->
    <v-dialog v-model="showAddServicioDialog" max-width="500">
      <v-card>
        <v-card-title>Agregar Servicio Clínico</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="nuevoServicio.nombre"
            label="Nombre del Servicio *"
            :rules="[v => !!v || 'Requerido']"
          ></v-text-field>
          <v-text-field
            v-model="nuevoServicio.codigo"
            label="Código"
          ></v-text-field>
          <v-textarea
            v-model="nuevoServicio.descripcion"
            label="Descripción"
            rows="2"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddServicioDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="agregarServicio" :loading="submitting">
            Agregar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/api'
import QRCode from 'qrcode'

export default {
  name: 'TransferenciaDM',
  data() {
    return {
      loading: false,
      submitting: false,
      showCreateDialog: false,
      showQRDialog: false,
      showScanDialog: false,
      showAddServicioDialog: false,
      filtroEstado: null,
      busqueda: '',
      transferencias: [],
      dispositivosDisponibles: [],
      serviciosClinicos: [],
      selectedTransferencia: null,
      qrCode: null,
      qrCodeScanned: '',
      nuevaTransferencia: {
        inventory_item_id: null,
        servicio_clinico_destino_id: null,
        usuario_receptor: '',
        fecha_estimada_recepcion: '',
        observaciones: ''
      },
      nuevoServicio: {
        nombre: '',
        codigo: '',
        descripcion: ''
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      headers: [
        { title: 'ID', key: 'id', sortable: true },
        { title: 'Dispositivo', key: 'dispositivo_nombre', sortable: true },
        { title: 'Servicio Destino', key: 'servicio_destino', sortable: true },
        { title: 'Fecha Transferencia', key: 'fecha_transferencia', sortable: true },
        { title: 'Estado', key: 'estado', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      estadosTransferencia: [
        { title: 'Pendiente', value: 'Pendiente' },
        { title: 'En Tránsito', value: 'En Tránsito' },
        { title: 'Recibida', value: 'Recibida' },
        { title: 'Cancelada', value: 'Cancelada' }
      ]
    }
  },
  watch: {
    showCreateDialog(newVal) {
      if (newVal) {
        // Limpiar formulario cuando se abre el diálogo
        this.resetNuevaTransferencia()
        // Recargar servicios clínicos para asegurar que estén actualizados
        this.fetchServiciosClinicos()
      }
    }
  },
  mounted() {
    this.fetchTransferencias()
    this.fetchDispositivosDisponibles()
    this.fetchServiciosClinicos()
  },
  methods: {
    async fetchTransferencias() {
      this.loading = true
      try {
        const params = {}
        if (this.filtroEstado) params.estado = this.filtroEstado
        if (this.busqueda) params.busqueda = this.busqueda

        const response = await apiClient.get('/dm/transferencias', { params })
        this.transferencias = response.data || []
      } catch (error) {
        console.error('Error al cargar transferencias:', error)
        this.showSnackbar('Error al cargar transferencias', 'error')
      } finally {
        this.loading = false
      }
    },

    async fetchDispositivosDisponibles() {
      try {
        const response = await apiClient.get('/dm/dispositivos-dm')
        this.dispositivosDisponibles = response.data.map(item => ({
          id: item.id,
          descripcion: `${item.product_name} - ${item.sku}`,
          lote: item.lot_number,
          serial_number: item.serial_number
        }))
      } catch (error) {
        console.error('Error al cargar dispositivos:', error)
      }
    },

    async fetchServiciosClinicos() {
      try {
        const response = await apiClient.get('/dm/servicios-clinicos')
        // El endpoint devuelve {success: true, data: [...], count: ...}
        if (response.data && response.data.success && response.data.data) {
          this.serviciosClinicos = response.data.data || []
        } else if (Array.isArray(response.data)) {
          // Fallback: si viene como array directo
          this.serviciosClinicos = response.data || []
        } else {
          console.warn('Formato de respuesta inesperado:', response.data)
          this.serviciosClinicos = []
        }
        console.log(`✅ Servicios clínicos cargados: ${this.serviciosClinicos.length}`)
      } catch (error) {
        console.error('Error al cargar servicios clínicos:', error)
        this.showSnackbar('Error al cargar servicios clínicos', 'error')
      }
    },

    async crearTransferencia() {
      if (!this.$refs.createForm.validate()) return

      // Validar que servicio_clinico_destino_id esté presente
      if (!this.nuevaTransferencia.servicio_clinico_destino_id) {
        this.showSnackbar('Debe seleccionar un servicio clínico destino', 'error')
        return
      }

      console.log('🔵 [TRANSFERENCIA] Datos a enviar:', {
        inventory_item_id: this.nuevaTransferencia.inventory_item_id,
        servicio_clinico_destino_id: this.nuevaTransferencia.servicio_clinico_destino_id,
        tipo: typeof this.nuevaTransferencia.servicio_clinico_destino_id
      })

      this.submitting = true
      try {
        const response = await apiClient.post('/dm/transferencias', this.nuevaTransferencia)
        
        console.log('✅ [TRANSFERENCIA] Respuesta del servidor:', response.data)
        
        // El endpoint devuelve {success: true, message: ..., data: {...}}
        const transferenciaData = response.data?.data || response.data
        
        // Generar QR usando el ID de la transferencia
        await this.generarQR(transferenciaData.id)
        
        this.showSnackbar('Transferencia creada exitosamente', 'success')
        this.showCreateDialog = false
        this.resetNuevaTransferencia()
        this.fetchTransferencias()
        this.$emit('refresh')
        
        // Mostrar QR
        this.selectedTransferencia = transferenciaData
        this.showQRDialog = true
      } catch (error) {
        console.error('❌ [TRANSFERENCIA] Error al crear transferencia:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Error al crear transferencia'
        this.showSnackbar(errorMessage, 'error')
      } finally {
        this.submitting = false
      }
    },

    async generarQR(transferenciaId) {
      try {
        const qrData = JSON.stringify({
          type: 'transferencia_dm',
          id: transferenciaId,
          timestamp: new Date().toISOString()
        })
        
        this.qrCode = await QRCode.toDataURL(qrData, {
          width: 400,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
      } catch (error) {
        console.error('Error al generar QR:', error)
      }
    },

    async showQR(transferencia) {
      this.selectedTransferencia = transferencia
      await this.generarQR(transferencia.id)
      this.showQRDialog = true
    },

    async recibirTransferencia(transferencia) {
      if (!confirm('¿Confirmar recepción de esta transferencia?')) return

      this.submitting = true
      try {
        await apiClient.put(`/dm/transferencias/${transferencia.id}/recibir`, {
          fecha_recepcion: new Date().toISOString()
        })
        
        this.showSnackbar('Transferencia recibida exitosamente', 'success')
        this.fetchTransferencias()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error al recibir transferencia:', error)
        this.showSnackbar('Error al recibir transferencia', 'error')
      } finally {
        this.submitting = false
      }
    },

    async procesarQRRecepcion() {
      if (!this.qrCodeScanned) {
        this.showSnackbar('Ingrese o escanee el código QR', 'warning')
        return
      }

      this.submitting = true
      try {
        const qrData = JSON.parse(this.qrCodeScanned)
        
        if (qrData.type !== 'transferencia_dm') {
          throw new Error('Código QR inválido')
        }

        await apiClient.put(`/dm/transferencias/${qrData.id}/recibir`, {
          fecha_recepcion: new Date().toISOString()
        })
        
        this.showSnackbar('Transferencia recibida exitosamente mediante QR', 'success')
        this.showScanDialog = false
        this.qrCodeScanned = ''
        this.fetchTransferencias()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error al procesar QR:', error)
        this.showSnackbar('Código QR inválido o transferencia no encontrada', 'error')
      } finally {
        this.submitting = false
      }
    },

    async agregarServicio() {
      if (!this.nuevoServicio.nombre) {
        this.showSnackbar('El nombre del servicio es requerido', 'warning')
        return
      }

      this.submitting = true
      try {
        const response = await apiClient.post('/dm/servicios-clinicos', this.nuevoServicio)
        
        // El endpoint devuelve {success: true, message: ..., data: {...}}
        const nuevoServicioData = response.data?.data || response.data
        
        // Recargar la lista completa de servicios
        await this.fetchServiciosClinicos()
        
        // Seleccionar el nuevo servicio en el formulario
        this.nuevaTransferencia.servicio_clinico_destino_id = nuevoServicioData.id
        
        this.showSnackbar('Servicio clínico agregado exitosamente', 'success')
        this.showAddServicioDialog = false
        this.nuevoServicio = { nombre: '', codigo: '', descripcion: '' }
      } catch (error) {
        console.error('Error al agregar servicio:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Error al agregar servicio clínico'
        this.showSnackbar(errorMessage, 'error')
      } finally {
        this.submitting = false
      }
    },

    verDetalle(transferencia) {
      // TODO: Implementar vista de detalle completo
      console.log('Ver detalle de transferencia:', transferencia)
    },

    downloadQR() {
      if (!this.qrCode) return

      const link = document.createElement('a')
      link.href = this.qrCode
      link.download = `QR-Transferencia-${this.selectedTransferencia.id}.png`
      link.click()
    },

    resetNuevaTransferencia() {
      this.nuevaTransferencia = {
        inventory_item_id: null,
        servicio_clinico_destino_id: null,
        usuario_receptor: '',
        fecha_estimada_recepcion: '',
        observaciones: ''
      }
      // Resetear validación del formulario
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation()
      }
    },

    cancelCreate() {
      this.showCreateDialog = false
      this.resetNuevaTransferencia()
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('es-ES')
    },

    getEstadoColor(estado) {
      const colors = {
        'Pendiente': 'warning',
        'En Tránsito': 'info',
        'Recibida': 'success',
        'Cancelada': 'error'
      }
      return colors[estado] || 'grey'
    },

    getEstadoLabel(estado) {
      // El backend ya devuelve valores legibles, así que solo devolvemos el estado tal cual
      return estado || 'N/A'
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

